import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SERVICES, SERVICE_GROUPS } from '@shared';
import { useLegal } from '../lib/legal-context';
import { useAuth } from '../lib/auth-context';
import { apiPost, ApiError } from '../lib/api';
import { PORTAL_ENABLED } from '../lib/feature-flags';
import { useSeo } from '../lib/seo';

interface FormState {
  company: string;
  country: 'UK' | 'AE' | 'EU' | 'IN' | 'SEA' | 'NA' | 'OT';
  name: string;
  email: string;
  service: string;
  brief: string;
  bodyCorp: boolean;
  sophisticated: boolean;
  notAdvice: boolean;
}

const INITIAL: FormState = {
  company: '', country: 'UK', name: '', email: '',
  service: 'sblc', brief: '',
  bodyCorp: false, sophisticated: false, notAdvice: false,
};

export function Contact() {
  useSeo({
    title: 'Submit a brief — Omega Trade and Finance',
    description: 'Body-corporate-only brief submission. Sophisticated / HNW self-certification required. Two-business-day response, written engagement letter before any work begins.',
    canonicalPath: '/contact',
  });
  const legal = useLegal();
  const { user } = useAuth();
  const isSignedInClient = user?.kind === 'client';
  const [params] = useSearchParams();
  const preselected = params.get('service');
  const initialForm: FormState = preselected && SERVICES.some((s) => s.id === preselected)
    ? { ...INITIAL, service: preselected }
    : INITIAL;
  const [form, setForm] = useState<FormState>(initialForm);
  const [reference, setReference] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = <K extends keyof FormState>(k: K) => (e: { target: { value: string } }) =>
    setForm((s) => ({ ...s, [k]: e.target.value as FormState[K] }));
  const toggle = (k: 'bodyCorp' | 'sophisticated' | 'notAdvice') => () =>
    setForm((s) => ({ ...s, [k]: !s[k] }));
  const eligibilityAllTicked = form.bodyCorp && form.sophisticated && form.notAdvice;
  const toggleEligibilityAll = () =>
    setForm((s) => {
      const next = !(s.bodyCorp && s.sophisticated && s.notAdvice);
      return { ...s, bodyCorp: next, sophisticated: next, notAdvice: next };
    });

  const missing: string[] = [];
  if (!form.company)              missing.push('Enter a company name.');
  if (!form.name)                 missing.push('Enter the authorised contact name.');
  if (!form.email)                missing.push('Enter a corporate email.');
  if (form.brief.length < 20)     missing.push(`Brief must be at least 20 characters (currently ${form.brief.length}).`);
  if (!eligibilityAllTicked)      missing.push('Review the conditions in the sophisticated-investor gate and click confirm.');
  const ready = missing.length === 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!ready) {
      // Surface the first missing requirement as an inline error so the
      // click feels acknowledged. The full list also shows above the button.
      setError(missing[0] ?? 'Some required fields are missing.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await apiPost<{ reference: string }>('/api/contact', {
        company: form.company,
        country: form.country,
        name: form.name,
        email: form.email,
        service: form.service,
        brief: form.brief,
        body_corporate_confirmed: form.bodyCorp,
        sophisticated_self_cert: form.sophisticated,
        not_advice_acknowledged: form.notAdvice,
      });
      setReference(r.reference);
    } catch (err) {
      const msg = err instanceof ApiError ? err.serverMessage : 'Submission failed. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <main>
        <section className="section" style={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
          <div className="container" style={{ textAlign: 'center' }}>
            <div style={{ maxWidth: 760, marginInline: 'auto' }}>
              <span className="eyebrow" style={{ justifyContent: 'center' }}>Brief received</span>
              <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', marginTop: 18, letterSpacing: '-0.025em' }}>
                Thank you — your brief is queued for triage.
              </h1>
              <p className="lead" style={{ marginTop: 18 }}>
                Reference <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg)' }}>{reference}</span> assigned.
                An Omega engagement lead will reach out to you by email within two UK business days.
              </p>
              <p style={{ marginTop: 12, fontSize: 13, color: 'var(--fg-mute)', maxWidth: '52ch', marginInline: 'auto', lineHeight: 1.6 }}>
                This is an inquiry, not a portal brief — it sits in our triage inbox until an engagement
                lead converts it into an engagement. You won't see it inside the client portal's
                <em> Requests</em> list.
              </p>
              <div style={{ marginTop: 28, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                {isSignedInClient
                  ? <Link className="btn accent" to="/portal">Back to portal</Link>
                  : PORTAL_ENABLED
                    ? <Link className="btn accent" to="/portal/sign-in">Apply for portal access</Link>
                    : <Link className="btn accent" to="/">Back to home</Link>}
                <button className="btn ghost" onClick={() => { setReference(null); setForm(INITIAL); }}>
                  Submit another
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="section" style={{ paddingBottom: 'var(--section-y)' }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 'clamp(32px, 5vw, 96px)' }}>
          <div>
            <span className="eyebrow">Submit a brief</span>
            <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', marginTop: 18, letterSpacing: '-0.025em' }}>
              Tell us what your corporate needs. We’ll respond in two business days.
            </h1>
            <p className="lead" style={{ marginTop: 18, maxWidth: '40ch' }}>
              Briefs are accepted from body corporates only. The form below carries the same
              gating you’ll see in the client portal.
            </p>

            <div style={{ marginTop: 40, padding: 24, background: 'var(--bg-soft)', borderRadius: 'var(--r-lg)', fontSize: 13.5, color: 'var(--fg-soft)', lineHeight: 1.65 }}>
              <strong style={{ color: 'var(--fg)' }}>Office</strong>
              <div style={{ marginTop: 8, fontFamily: 'var(--mono)', fontSize: 12 }}>+44 20 8050 7540</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>info@omegatrade.co.uk</div>
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--fg-mute)' }}>General office line. For new briefs, use the form on the right.</div>
              <hr className="rule" style={{ margin: '18px 0' }} />
              <strong style={{ color: 'var(--fg)' }}>Offices</strong>
              <div style={{ marginTop: 8 }}>London · Dubai · India</div>
            </div>
          </div>

          <div className="card" style={{ padding: 'clamp(28px, 4vw, 48px)' }}>
            {isSignedInClient && (
              <div style={{
                marginBottom: 24, padding: 16,
                background: 'var(--accent-soft)', borderRadius: 'var(--r)',
                fontSize: 13.5, lineHeight: 1.55,
              }}>
                <strong style={{ color: 'var(--fg)' }}>You're already signed in as a client.</strong>{' '}
                Briefs submitted on this public form land in our triage inbox and are <em>not</em> linked
                to your portal account. To submit a brief that appears in your portal under <em>Requests</em>,{' '}
                <Link to={`/portal/requests/new${preselected ? `?service=${preselected}` : ''}`}
                      style={{ color: 'var(--accent)', fontWeight: 500 }}>
                  use the portal form →
                </Link>
              </div>
            )}
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="field">
                  <label>Company name<span className="req">*</span></label>
                  <input required value={form.company} onChange={setField('company')} placeholder="Helios Marine Ltd" />
                </div>
                <div className="field">
                  <label>Jurisdiction<span className="req">*</span></label>
                  <select value={form.country} onChange={setField('country')}>
                    <option value="UK">United Kingdom</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="EU">European Union</option>
                    <option value="IN">India</option>
                    <option value="SEA">Southeast Asia</option>
                    <option value="NA">North America</option>
                    <option value="OT">Other</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="field">
                  <label>Authorised contact<span className="req">*</span></label>
                  <input required value={form.name} onChange={setField('name')} placeholder="J. Carter, CFO" />
                </div>
                <div className="field">
                  <label>Corporate email<span className="req">*</span></label>
                  <input required type="email" value={form.email} onChange={setField('email')} placeholder="cfo@heliosmarine.co.uk" />
                </div>
              </div>

              <div className="field">
                <label>Service of interest</label>
                <select value={form.service} onChange={setField('service')}>
                  {SERVICE_GROUPS.map((g) => (
                    <optgroup key={g.id} label={g.label}>
                      {SERVICES.filter((s) => s.group === g.id).map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Brief<span className="req">*</span></label>
                <textarea
                  required
                  minLength={20}
                  value={form.brief}
                  onChange={setField('brief')}
                  placeholder="Tell us about the corporate, the transaction or research need, and timing."
                  rows={4}
                />
                <div className="hint">
                  Do not include sensitive personal data. We’ll request KYC documentation through the secure portal. Minimum 20 characters.
                </div>
              </div>

              <hr className="rule" />

              <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', letterSpacing: 0.06, textTransform: 'uppercase' }}>
                Sophisticated-investor gate
              </div>

              <ul style={{
                margin: 0,
                paddingLeft: 22,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                fontSize: 13.5,
                color: 'var(--fg-soft)',
                lineHeight: 1.55,
              }}>
                <li>{splitFirstSentence(legal.contact_eligibility_body_corp)}</li>
                <li>{splitFirstSentence(legal.contact_eligibility_sophisticated)}</li>
                <li>{splitFirstSentence(legal.contact_eligibility_not_advice)}</li>
              </ul>

              <label
                className={`checkbox ${eligibilityAllTicked ? 'on' : ''}`}
                onClick={toggleEligibilityAll}
              >
                <span className="box" />
                <span>I confirm all the eligibility criteria are met.</span>
              </label>

              {error && (
                <div style={{ padding: 12, borderRadius: 'var(--r)', background: 'rgba(179,38,30,.10)', color: 'var(--red)', fontSize: 13 }}>
                  {error}
                </div>
              )}

              {!ready && missing.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
                  To enable submit:
                  <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                    {missing.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                </div>
              )}

              <button
                type="submit"
                className="btn accent"
                style={{ marginTop: 8, alignSelf: 'flex-start' }}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Submit brief →'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}

// Render the first sentence of an eligibility statement in a heavier weight so
// the headline claim stands out from the supporting detail. Falls back to the
// raw text if no leading sentence boundary is found.
function splitFirstSentence(text: string) {
  const m = text.match(/^([^.]+\.)\s*(.*)$/);
  if (!m) return text;
  return (
    <>
      <strong style={{ color: 'var(--fg)', fontWeight: 600 }}>{m[1]}</strong>{' '}{m[2]}
    </>
  );
}
